import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import type { ChartType, SlideElement } from '../../types/slide';
import { BRAND, FONT_STACKS } from '../../lib/brand';

interface Props {
  el: SlideElement;
  selected: boolean;
  scale: number;
  onSelect: (id: string, opts?: { additive?: boolean }) => void;
  onChangeText: (id: string, text: string) => void;
  onDragEnd: (id: string, x: number, y: number) => void;
  /** Called once, when a drag actually starts moving (for a single undo step). */
  onDragStart?: (id: string) => void;
}

export function SlideElementView({
  el,
  selected,
  scale,
  onSelect,
  onChangeText,
  onDragEnd,
  onDragStart,
}: Props) {
  const style: CSSProperties = {
    position: 'absolute',
    left: el.x,
    top: el.y,
    width: el.w,
    height: el.h,
    zIndex: el.zIndex || 1,
    outline: selected ? `2px solid ${BRAND.bright}` : undefined,
    outlineOffset: 2,
    cursor: el.locked ? 'default' : 'move',
    userSelect: el.type === 'text' ? 'text' : 'none',
  };

  const onPointerDown = (e: ReactPointerEvent) => {
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    if (el.locked) {
      onSelect(el.id, { additive });
      return;
    }
    e.stopPropagation();
    onSelect(el.id, { additive });
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = el.x;
    const origY = el.y;
    let started = false;

    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / scale;
      const dy = (ev.clientY - startY) / scale;
      if (!started) {
        if (Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
        started = true;
        onDragStart?.(el.id);
      }
      const nx = Math.round((origX + dx) / 8) * 8;
      const ny = Math.round((origY + dy) / 8) * 8;
      onDragEnd(el.id, nx, ny);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  if ((el.type === 'image' || el.type === 'logo') && el.src) {
    return (
      <div style={{ ...style, overflow: 'hidden', borderRadius: el.radius ?? 8 }} onPointerDown={onPointerDown}>
        <img
          src={el.src}
          alt=""
          draggable={false}
          style={{ display: 'block', width: '100%', height: '100%', objectFit: el.fit ?? 'cover' }}
        />
      </div>
    );
  }

  if (el.type === 'text' || el.type === 'footer' || el.type === 'slide_number' || el.type === 'logo') {
    return (
      <div
        style={{
          ...style,
          color: el.color,
          fontSize: el.fontSize,
          fontWeight: el.fontWeight,
          fontFamily: FONT_STACKS[el.font ?? 'body'],
          textAlign: el.align || 'left',
          whiteSpace: 'pre-wrap',
          lineHeight: 1.35,
        }}
        onPointerDown={onPointerDown}
        contentEditable={!el.locked}
        suppressContentEditableWarning
        onBlur={(e) => onChangeText(el.id, e.currentTarget.innerText)}
        className="outline-none"
      >
        {el.text}
      </div>
    );
  }

  if (el.type === 'shape') {
    return (
      <div
        style={{ ...style, background: el.fill || BRAND.blue, borderRadius: el.radius || 0 }}
        onPointerDown={onPointerDown}
      />
    );
  }

  if (el.type === 'kpi_card') {
    return (
      <div
        style={{
          ...style,
          background: el.fill || BRAND.surface,
          borderRadius: el.radius || 12,
          border: `1px solid ${BRAND.sky}`,
          padding: 28,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
        onPointerDown={onPointerDown}
      >
        <div style={{ fontSize: 56, fontWeight: 700, color: BRAND.blue, lineHeight: 1 }}>
          {el.metric}
        </div>
        <div style={{ marginTop: 14, fontSize: 22, color: BRAND.ink, fontWeight: 600 }}>
          {el.label}
        </div>
        {el.delta && (
          <div style={{ marginTop: 8, fontSize: 18, color: BRAND.bright, fontWeight: 700 }}>
            {el.delta}
          </div>
        )}
      </div>
    );
  }

  if (el.type === 'chart') {
    return (
      <div
        style={{
          ...style,
          background: el.fill || BRAND.surface,
          borderRadius: 12,
          border: `1px solid ${BRAND.sky}`,
          padding: 20,
        }}
        onPointerDown={onPointerDown}
      >
        <MiniChart
          type={el.chartType || 'bar'}
          labels={el.chartLabels || []}
          values={el.chartValues || []}
        />
      </div>
    );
  }

  if (el.type === 'timeline') {
    const ms = el.milestones || [];
    return (
      <div style={{ ...style, padding: '24px 8px' }} onPointerDown={onPointerDown}>
        <div className="relative h-full">
          <div
            className="absolute left-0 right-0 top-1/2 h-0.5"
            style={{ background: BRAND.blue }}
          />
          <div className="relative flex h-full justify-between">
            {ms.map((m) => (
              <div key={m.date + m.title} className="flex w-full flex-col items-center px-2">
                <div className="mb-auto text-center text-sm font-bold" style={{ color: BRAND.blue }}>
                  {m.date}
                </div>
                <div
                  className="my-auto h-4 w-4 rounded-full border-4 border-white"
                  style={{ background: BRAND.bright }}
                />
                <div className="mt-auto text-center">
                  <div className="text-sm font-bold" style={{ color: BRAND.ink }}>
                    {m.title}
                  </div>
                  <div className="text-xs" style={{ color: BRAND.gray }}>
                    {m.detail}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (el.type === 'pros_cons') {
    return (
      <div
        style={{
          ...style,
          background: el.fill || BRAND.surface,
          borderRadius: 12,
          border: `1px solid ${BRAND.sky}`,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          padding: 20,
        }}
        onPointerDown={onPointerDown}
      >
        <div>
          <div className="mb-2 text-sm font-bold" style={{ color: BRAND.bright }}>
            Pros
          </div>
          <ul className="space-y-1 text-sm" style={{ color: BRAND.ink }}>
            {(el.pros || []).map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="mb-2 text-sm font-bold" style={{ color: BRAND.gray }}>Cons</div>
          <ul className="space-y-1 text-sm" style={{ color: BRAND.ink }}>
            {(el.cons || []).map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  if (el.type === 'callout') {
    return (
      <div
        style={{
          ...style,
          background: el.fill || BRAND.surface,
          borderRadius: el.radius ?? 8,
          border: `1px solid ${BRAND.sky}`,
          padding: '20px 24px',
          color: BRAND.ink,
          fontSize: 16,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
        }}
        onPointerDown={onPointerDown}
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => onChangeText(el.id, e.currentTarget.innerText)}
      >
        {el.text}
      </div>
    );
  }

  if (el.type === 'process_row') {
    const steps = el.steps || [];
    return (
      <div style={{ ...style, display: 'grid', gridTemplateColumns: `repeat(${steps.length}, 1fr)`, gap: 12 }} onPointerDown={onPointerDown}>
        {steps.map((s, i) => (
          <div
            key={s.title}
            className="rounded-lg p-4"
            style={{ background: BRAND.surface, border: `1px solid ${BRAND.sky}` }}
          >
            <div className="text-sm font-bold" style={{ color: BRAND.ink }}>
              {i + 1}. {s.title}
            </div>
            <div className="mt-2 text-xs" style={{ color: BRAND.gray }}>
              {s.body}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (el.type === 'quote_block') {
    return (
      <div
        style={{
          ...style,
          background: el.fill || BRAND.surface,
          borderRadius: el.radius ?? 8,
          border: `1px solid ${BRAND.sky}`,
          padding: '28px 32px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 16,
        }}
        onPointerDown={onPointerDown}
      >
        <div
          style={{
            fontFamily: FONT_STACKS.display,
            fontSize: Math.min(32, Math.max(18, el.w / 22)),
            fontWeight: 600,
            color: BRAND.ink,
            lineHeight: 1.35,
          }}
        >
          {el.text}
        </div>
        {el.attribution && (
          <div style={{ fontSize: 16, fontWeight: 600, color: BRAND.bright }}>{el.attribution}</div>
        )}
      </div>
    );
  }

  if (el.type === 'checklist') {
    return (
      <div
        style={{
          ...style,
          background: el.fill || BRAND.surface,
          borderRadius: el.radius ?? 8,
          border: `1px solid ${BRAND.sky}`,
          padding: '20px 24px',
        }}
        onPointerDown={onPointerDown}
      >
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(el.items || []).map((item) => (
            <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 16, color: BRAND.ink }}>
              <span
                style={{
                  flexShrink: 0,
                  width: 18,
                  height: 18,
                  marginTop: 2,
                  borderRadius: 4,
                  border: `2px solid ${BRAND.bright}`,
                  background: BRAND.white,
                }}
              />
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (el.type === 'stat_row') {
    const stats = el.stats || [];
    return (
      <div
        style={{
          ...style,
          background: el.fill || BRAND.surface,
          borderRadius: el.radius ?? 8,
          border: `1px solid ${BRAND.sky}`,
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.max(stats.length, 1)}, 1fr)`,
          gap: 8,
          padding: '16px 12px',
        }}
        onPointerDown={onPointerDown}
      >
        {stats.map((s) => (
          <div key={s.label + s.metric} style={{ textAlign: 'center', padding: '4px 8px' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: BRAND.blue, lineHeight: 1.1 }}>{s.metric}</div>
            <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: BRAND.gray }}>{s.label}</div>
          </div>
        ))}
      </div>
    );
  }

  if (el.type === 'team_card') {
    return (
      <div
        style={{
          ...style,
          background: el.fill || BRAND.surface,
          borderRadius: el.radius ?? 8,
          border: `1px solid ${BRAND.sky}`,
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 8,
        }}
        onPointerDown={onPointerDown}
      >
        <div style={{ fontSize: 22, fontWeight: 700, color: BRAND.ink }}>{el.text || 'Name'}</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: BRAND.bright }}>{el.role || 'Role'}</div>
        {el.contact && <div style={{ fontSize: 13, color: BRAND.gray }}>{el.contact}</div>}
      </div>
    );
  }

  return null;
}

const CHART_COLORS = [BRAND.blue, BRAND.sky, BRAND.bright, BRAND.gray, '#4A90C8', '#6B8FA3'];

function MiniChart({
  type,
  labels,
  values,
}: {
  type: ChartType;
  labels: string[];
  values: number[];
}) {
  const max = Math.max(...values, 1);
  if (type === 'donut' || type === 'pie') {
    const total = values.reduce((a, b) => a + b, 0) || 1;
    let acc = 0;
    const stops = values.map((v, i) => {
      const start = (acc / total) * 360;
      acc += v;
      const end = (acc / total) * 360;
      return `${CHART_COLORS[i % CHART_COLORS.length]} ${start}deg ${end}deg`;
    });
    const hole = type === 'donut';
    return (
      <div className="flex h-full items-center justify-center gap-6">
        <div
          className="h-40 w-40 rounded-full"
          style={{
            background: `conic-gradient(${stops.join(',')})`,
            ...(hole
              ? {
                  mask: 'radial-gradient(circle, transparent 45%, black 46%)',
                  WebkitMask: 'radial-gradient(circle, transparent 45%, black 46%)',
                }
              : {}),
          }}
        />
        <ul className="space-y-1 text-sm">
          {labels.map((l, i) => (
            <li key={l} className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
              />
              {l}: {values[i]}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (type === 'line' || type === 'area') {
    const pts = values.map((v, i) => {
      const x = (i / Math.max(values.length - 1, 1)) * 100;
      const y = 100 - (v / max) * 85;
      return { x, y };
    });
    const linePts = pts.map((p) => `${p.x},${p.y}`).join(' ');
    const areaPts =
      pts.length > 0
        ? `0,100 ${linePts} 100,100`
        : '';
    return (
      <svg viewBox="0 0 100 100" className="h-full w-full" preserveAspectRatio="none">
        {type === 'area' && areaPts && (
          <polygon fill={BRAND.sky} fillOpacity={0.45} points={areaPts} />
        )}
        <polyline
          fill="none"
          stroke={BRAND.blue}
          strokeWidth="2"
          points={linePts}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  }

  if (type === 'hbar') {
    return (
      <div className="flex h-full flex-col justify-center gap-2 px-2 py-1">
        {values.map((v, i) => (
          <div key={labels[i] || i} className="flex items-center gap-2">
            <span
              className="w-12 shrink-0 truncate text-right text-[10px] font-semibold"
              style={{ color: BRAND.gray }}
            >
              {labels[i]}
            </span>
            <div className="h-4 flex-1 overflow-hidden rounded-sm" style={{ background: '#DCE6F1' }}>
              <div
                className="h-full rounded-sm"
                style={{ width: `${(v / max) * 100}%`, background: BRAND.blue, minWidth: 4 }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-full items-end gap-3 px-2 pb-2">
      {values.map((v, i) => (
        <div key={labels[i] || i} className="flex flex-1 flex-col items-center gap-2">
          <div
            className="w-full rounded-t"
            style={{
              height: `${(v / max) * 100}%`,
              minHeight: 8,
              background: BRAND.blue,
            }}
          />
          <span className="text-[10px] font-semibold" style={{ color: BRAND.gray }}>
            {labels[i]}
          </span>
        </div>
      ))}
    </div>
  );
}
